class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener(
      "keyup",
      (evt) => evt.code === "Escape" && this.close()
    );
    this.querySelector("#CartDrawer-Overlay").addEventListener(
      "click",
      this.close.bind(this)
    );
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector("#cart-icon-bubble");
    cartLink.setAttribute("role", "button");
    cartLink.setAttribute("aria-haspopup", "dialog");
    cartLink.addEventListener("click", (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener("keydown", (event) => {
      if (event.code.toUpperCase() === "SPACE") {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  async open(triggeredBy) {
    try {
      if (triggeredBy) this.setActiveElement(triggeredBy);
      const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
      if (cartDrawerNote && !cartDrawerNote.hasAttribute("role"))
        this.setSummaryAccessibility(cartDrawerNote);

      // Add classes and wait for animation
      this.classList.add("animate", "active");

      // Wait for transition to complete
      await this.waitForTransition();

      const containerToTrapFocusOn = this.classList.contains("is-empty")
        ? this.querySelector(".drawer__inner-empty")
        : document.getElementById("CartDrawer");
      const focusElement =
        this.querySelector(".drawer__inner") ||
        this.querySelector(".drawer__close");
      trapFocus(containerToTrapFocusOn, focusElement);

      // Dispatch event when cart drawer is fully opened
      document.dispatchEvent(new CustomEvent("cart-drawer:opened"));

      document.body.classList.add("overflow-hidden");
    } catch (error) {
      console.warn("Cart drawer open failed:", error);
      // Fallback to original behavior
      this.classList.add("animate", "active");
      document.body.classList.add("overflow-hidden");
    }
  }

  async waitForTransition() {
    return new Promise((resolve) => {
      const handleTransitionEnd = () => {
        this.removeEventListener("transitionend", handleTransitionEnd);
        resolve();
      };

      this.addEventListener("transitionend", handleTransitionEnd, {
        once: true,
      });

      // Fallback in case transitionend doesn't fire
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          if (!this.classList.contains("active")) {
            setTimeout(resolve, 300); // Fallback timeout
          }
        });
      });
    });
  }

  close() {
    this.classList.remove("active");
    removeTrapFocus(this.activeElement);
    document.body.classList.remove("overflow-hidden");
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute("role", "button");
    cartDrawerNote.setAttribute("aria-expanded", "false");

    if (cartDrawerNote.nextElementSibling.getAttribute("id")) {
      cartDrawerNote.setAttribute(
        "aria-controls",
        cartDrawerNote.nextElementSibling.id
      );
    }

    cartDrawerNote.addEventListener("click", (event) => {
      event.currentTarget.setAttribute(
        "aria-expanded",
        !event.currentTarget.closest("details").hasAttribute("open")
      );
    });

    cartDrawerNote.parentElement.addEventListener("keyup", onKeyUpEscape);
  }

  async renderContents(parsedState) {
    try {
      this.querySelector(".drawer__inner").classList.contains("is-empty") &&
        this.querySelector(".drawer__inner").classList.remove("is-empty");
      this.productId = parsedState.id;

      // Update sections
      this.getSectionsToRender().forEach((section) => {
        const sectionElement = section.selector
          ? document.querySelector(section.selector)
          : document.getElementById(section.id);
        sectionElement.innerHTML = this.getSectionInnerHTML(
          parsedState.sections[section.id],
          section.selector
        );
      });

      // Wait for DOM updates to complete
      await this.waitForDOMUpdate();

      // Dispatch cart drawer content updated event
      document.dispatchEvent(
        new CustomEvent("cart-drawer-content-updated", {
          detail: { source: "cart-drawer-render", cart: parsedState },
        })
      );

      // Add event listeners and open drawer
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();

      // Wait for drawer to open and then refresh free shipping goals
      await this.waitForDrawerOpen();
      await this.refreshFreeShippingGoals();
    } catch (error) {
      console.warn("Cart drawer render contents failed:", error);
      // Fallback to original behavior - just open the drawer
      this.querySelector("#CartDrawer-Overlay").addEventListener(
        "click",
        this.close.bind(this)
      );
      this.open();
    }
  }

  async waitForDOMUpdate() {
    return new Promise((resolve) => {
      // Use requestAnimationFrame to wait for DOM updates
      requestAnimationFrame(() => {
        requestAnimationFrame(resolve);
      });
    });
  }

  async waitForDrawerOpen() {
    return new Promise((resolve) => {
      if (this.classList.contains("active")) {
        resolve();
      } else {
        const observer = new MutationObserver((mutations) => {
          mutations.forEach((mutation) => {
            if (
              mutation.type === "attributes" &&
              mutation.attributeName === "class" &&
              this.classList.contains("active")
            ) {
              observer.disconnect();
              resolve();
            }
          });
        });
        observer.observe(this, { attributes: true });
      }
    });
  }

  async refreshFreeShippingGoals() {
    try {
      const freeShippingGoals = document.querySelectorAll(
        "free-progress-shipping-goal"
      );

      // Use fast refresh for immediate response
      freeShippingGoals.forEach((goal) => {
        if (goal.refreshCartTotalFast) {
          goal.refreshCartTotalFast();
        }
      });
    } catch (error) {
      console.warn("Free shipping goals refresh failed:", error);
      // Don't let this break the cart drawer functionality
    }
  }

  getSectionInnerHTML(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: "cart-drawer",
        selector: "#CartDrawer",
      },
      {
        id: "cart-icon-bubble",
      },
    ];
  }

  getSectionDOM(html, selector = ".shopify-section") {
    return new DOMParser()
      .parseFromString(html, "text/html")
      .querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define("cart-drawer", CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: "CartDrawer",
        section: "cart-drawer",
        selector: ".drawer__inner",
      },
      {
        id: "cart-icon-bubble",
        section: "cart-icon-bubble",
        selector: ".shopify-section",
      },
    ];
  }
}

customElements.define("cart-drawer-items", CartDrawerItems);
